import { supabaseClient } from './supabase';
import type { PermissionRow, RolePermissionRow } from '@/types/organizations';
import { maybeThrowError } from '@/utils/error-helpers';
import decamelizeKeys from 'decamelize-keys';

export async function loadAllPermissions(): Promise<PermissionRow[]> {
    const { data, error } = await supabaseClient.from('permissions').select('*');
    if (error) {
        console.error(error);
    }
    return data ?? [];
}

export async function loadOrgRolePermissions(orgId: string): Promise<RolePermissionRow[]> {
    const { data, error } = await supabaseClient
        .from('role_permissions')
        .select('*')
        .eq('organization_id', orgId);
    if (error) {
        console.error(error);
        return [];
    }
    return (data ?? []).map((rp) => ({
        id: rp.id,
        organizationId: rp.organization_id ?? '',
        permissionId: rp.permission_id ?? '',
        role: rp.role,
        allowed: rp.allowed,
    }));
}

export async function saveRolePermissions(rolePermissions: RolePermissionRow[]) {
    const payload = rolePermissions.map((rp) => ({
        ...rp,
        organization_id: rp.organizationId === '' ? null : rp.organizationId,
        permission_id: rp.permissionId === '' ? null : rp.permissionId,
    }));
    const { data, error } = await supabaseClient
        .from('role_permissions')
        .upsert(decamelizeKeys(payload), { onConflict: 'organization_id,role,permission_id' });
    if (error) throw error;
    return data;
}

export async function populateOrgRolePermissions(organizationId: string): Promise<void> {
    const { error } = await supabaseClient.functions.invoke('org-role-permissions/populate', {
        body: { organizationId },
    });
    maybeThrowError(error, 'errors.frontend.general');
}

export async function resetOrgRolePermissions(organizationId: string): Promise<void> {
    const { error } = await supabaseClient.functions.invoke('org-role-permissions/reset', {
        body: { organizationId },
    });
    maybeThrowError(error, 'errors.frontend.general');
}

export async function loadRolePermissions(orgId: string, role: string): Promise<RolePermissionRow[]> {
    const { data, error } = await supabaseClient
        .from("role_permissions")
        .select("*")
        .eq("organization_id", orgId)
        .eq("role", role);

    if (error) {
        console.error(error);
        return [];
    }

    return (data ?? []).map((rp) => ({
        id: rp.id,
        organizationId: rp.organization_id ?? "",
        permissionId: rp.permission_id ?? "",
        role: rp.role,
        allowed: rp.allowed,
    }));
}
